import type { PermissionDetail, RiskLevel } from '~/api/types';
import { Badge, classes } from '~/shared/ui';

/**
 * What an application can reach, grouped and in plain language (§25).
 *
 * Two decisions matter here. High-risk permissions sort to the top, because a list ordered by
 * anything else buries the one item that should change someone's mind. And a permission the analyzer
 * detected in the source but the developer did not declare is labelled as such — that gap is the
 * single most useful signal on the page.
 */
export function PermissionList({
  permissions,
}: {
  permissions: readonly PermissionDetail[];
}): JSX.Element {
  if (permissions.length === 0) {
    return (
      <p className="text-sm text-slate-400">
        This application does not request access to anything in your account.
      </p>
    );
  }

  const grouped = groupByGroup(permissions);

  return (
    <div className="space-y-6">
      {grouped.map(([group, entries]) => (
        <div key={group}>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {group}
          </h4>
          <ul className="space-y-2">
            {entries.map((entry) => (
              <li
                key={entry.permission}
                className={classes(
                  'rounded-lg border p-3',
                  entry.risk === 'HIGH'
                    ? 'border-red-500/30 bg-red-500/5'
                    : 'border-slate-800 bg-slate-900/40',
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-200">{entry.label}</span>
                  <RiskDot risk={entry.risk} />
                  {entry.origin === 'DETECTED' ? (
                    <Badge tone="warn" title="Found in the source code but not declared by the developer">
                      Detected in source
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-slate-400">{entry.explanation}</p>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function RiskDot({ risk }: { risk: RiskLevel }): JSX.Element {
  const tone = {
    LOW: 'bg-risk-low',
    MEDIUM: 'bg-risk-medium',
    HIGH: 'bg-risk-high',
  }[risk];
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-400">
      <span aria-hidden="true" className={classes('h-1.5 w-1.5 rounded-full', tone)} />
      {risk.toLowerCase()}
    </span>
  );
}

const RISK_ORDER: Readonly<Record<RiskLevel, number>> = { HIGH: 0, MEDIUM: 1, LOW: 2 };

function groupByGroup(
  permissions: readonly PermissionDetail[],
): readonly [string, readonly PermissionDetail[]][] {
  const groups = new Map<string, PermissionDetail[]>();
  for (const permission of permissions) {
    const bucket = groups.get(permission.group);
    if (bucket === undefined) groups.set(permission.group, [permission]);
    else bucket.push(permission);
  }

  return [...groups.entries()]
    .map(
      ([group, entries]) =>
        [group, [...entries].sort((left, right) => RISK_ORDER[left.risk] - RISK_ORDER[right.risk])] as [
          string,
          readonly PermissionDetail[],
        ],
    )
    .sort(([, left], [, right]) => RISK_ORDER[left[0]?.risk ?? 'LOW'] - RISK_ORDER[right[0]?.risk ?? 'LOW']);
}
